import { CheckCircle, Filter } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { nativeFilter } from 'sprites-types/workflow/tools/nativeFilter'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { SmartValueDisplay } from '../shared/SmartValueDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function NativeFilterDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(nativeFilter, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const itemCount = Array.isArray(result) ? result.length : 1
  const displayItems = Array.isArray(result) ? result.slice(0, 10) : [result]

  return (
    <ContextMenuWrapper actions={[]}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 border-b border-gray-200/50 px-4 py-3">
          <div className="flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">Data filtered successfully</div>
            <div className="text-xs text-gray-600">
              {itemCount} item{itemCount !== 1 ? 's' : ''} matched
            </div>
          </div>
          <div className="flex-shrink-0">
            <Filter className="h-5 w-5 text-gray-400" />
          </div>
        </div>

        <div className="px-4 py-3">
          <div className="mb-2 text-xs font-semibold text-gray-500">FILTERED ITEMS</div>
          <div className="space-y-2">
            {displayItems.map((item, index) => (
              <SmartValueDisplay key={index} value={item} maxLength={200} />
            ))}
            {Array.isArray(result) && result.length > 10 && (
              <div className="py-2 text-center text-xs text-gray-500">
                ... and {result.length - 10} more items
              </div>
            )}
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadNativeFilter(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function nativeFilterToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(nativeFilter, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  const itemCount = Array.isArray(result) ? result.length : 1
  const items = Array.isArray(result) ? result : [result]

  let markdown = `## Data Filtered Successfully

**Items matched:** ${itemCount}

### Results:

`

  items.slice(0, 10).forEach((item, index) => {
    markdown += `**Item ${index + 1}:**\n`
    if (typeof item === 'string') {
      markdown += `${item}\n\n`
    } else {
      markdown += `\`\`\`json\n${JSON.stringify(item, null, 2)}\n\`\`\`\n\n`
    }
  })

  if (items.length > 10) {
    markdown += `... and ${items.length - 10} more items\n\n`
  }

  return markdown + '---\n'
}

export const NativeFilterDisplay = createWorkflowToolComponent(
  NativeFilterDisplayComponent,
  downloadNativeFilter,
  nativeFilterToMarkdown
)
